import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "react-query";
import { AlertCircle, ArrowDownLeft, ArrowUpRight, Coins, X } from "lucide-react";
import { fetchAssets, fetchCurrencies, fetchInvestments } from "../../utilities/api";
import { cn } from "../../utilities/helpers";
import { Button } from "../Button";
import { getSignedUnits, humanizeAssetLabel, normalizeAsset } from "../home/shared";

const COMMON_CURRENCIES = ["USD", "CHF", "EUR", "GBP", "INR"];

const TYPE_OPTIONS = [
	{ value: "BUY", label: "Buy", icon: ArrowDownLeft },
	{ value: "SELL", label: "Sell", icon: ArrowUpRight },
	{ value: "DIVIDEND", label: "Dividend", icon: Coins },
];

export const CONTROL_CLASS =
	"h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 aria-[invalid=true]:border-rose-400 aria-[invalid=true]:focus-visible:ring-rose-300";

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const formatMoney = (amount, code) => {
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: code || "USD",
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `${code || ""} ${Number(amount || 0).toFixed(2)}`.trim();
	}
};

const formatUnits = (units) =>
	Number(units || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

// Modal shell: portalled to <body> because the app header uses backdrop-blur,
// which would otherwise become the containing block for a fixed overlay and
// clip the dialog to the header's height.
export const Dialog = ({ open, onClose, title, description, children, footer, onSubmit }) => {
	const panelRef = useRef(null);
	const titleId = useId();
	const descriptionId = useId();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (!open) return undefined;
		const previouslyFocused = document.activeElement;
		const { overflow } = document.body.style;
		document.body.style.overflow = "hidden";

		const panel = panelRef.current;
		const initial =
			panel?.querySelector("[data-autofocus]") || panel?.querySelector(FOCUSABLE);
		initial?.focus();

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				onCloseRef.current();
				return;
			}
			if (event.key !== "Tab" || !panel) return;
			const nodes = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
				(node) => node.offsetParent !== null
			);
			if (!nodes.length) return;
			const first = nodes[0];
			const last = nodes[nodes.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = overflow;
			if (previouslyFocused && typeof previouslyFocused.focus === "function") {
				previouslyFocused.focus();
			}
		};
	}, [open]);

	if (!open) return null;

	return createPortal(
		<div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
			<div
				className="absolute inset-0 bg-slate-950/50"
				aria-hidden="true"
				onClick={onClose}
			/>
			<form
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				aria-describedby={description ? descriptionId : undefined}
				noValidate
				onSubmit={onSubmit}
				className="relative flex max-h-[92dvh] w-full flex-col rounded-t-xl border border-slate-200 bg-white shadow-xl sm:max-h-[85vh] sm:max-w-lg sm:rounded-xl"
			>
				<div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
					<div>
						<h2 id={titleId} className="text-base font-semibold text-slate-900">
							{title}
						</h2>
						{description && (
							<p id={descriptionId} className="mt-1 text-sm text-slate-500">
								{description}
							</p>
						)}
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="-mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
					>
						<X className="h-4 w-4" aria-hidden="true" />
					</button>
				</div>
				<div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
				{footer && (
					<div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-3 sm:flex-row sm:justify-end sm:rounded-b-xl">
						{footer}
					</div>
				)}
			</form>
		</div>,
		document.body
	);
};

const Field = ({ id, label, hint, error, children, className }) => (
	<div className={cn("space-y-1.5", className)}>
		<label htmlFor={id} className="block text-sm font-medium text-slate-700">
			{label}
		</label>
		{children}
		{error ? (
			<p id={`${id}-error`} className="flex items-center gap-1 text-xs text-rose-700">
				<AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
				{error}
			</p>
		) : (
			hint && (
				<p id={`${id}-hint`} className="text-xs text-slate-500">
					{hint}
				</p>
			)
		)}
	</div>
);

const describedBy = (id, error, hint) =>
	error ? `${id}-error` : hint ? `${id}-hint` : undefined;

export const validateTransaction = (form) => {
	const errors = {};
	const isDividend = form.transactionType === "DIVIDEND";
	if (!form.asset.trim()) errors.asset = "Choose or type an asset";
	if (!form.transactionDate) errors.transactionDate = "Pick a date";
	if (!/^[A-Za-z]{3}$/.test(form.currency)) errors.currency = "Pick a currency";

	const value = parseFloat(form.value);
	if (form.value === "" || !Number.isFinite(value)) {
		errors.value = isDividend ? "Enter the amount received" : "Enter the price per unit";
	} else if (isDividend ? value <= 0 : value < 0) {
		errors.value = isDividend ? "Amount must be greater than 0" : "Price can't be negative";
	}

	if (!isDividend) {
		const units = parseFloat(form.units);
		if (form.units === "" || !Number.isFinite(units)) errors.units = "Enter the number of units";
		else if (units <= 0) errors.units = "Units must be greater than 0";
	}
	return errors;
};

// Shared Buy / Sell / Dividend form used by both the add and edit dialogs.
export const TransactionFormDialog = ({
	open,
	onClose,
	title,
	description,
	initialValues,
	allowDividend = false,
	excludeId,
	submitLabel,
	isSubmitting,
	serverError,
	onSubmit,
}) => {
	const idPrefix = useId();
	const fieldId = (name) => `${idPrefix}-${name}`;
	const [form, setForm] = useState(initialValues);
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);
	// The parent rebuilds initialValues on every render; a ref keeps it out of the
	// reset effect's deps so typing is never clobbered mid-edit.
	const initialValuesRef = useRef(initialValues);
	initialValuesRef.current = initialValues;

	const { data: assets } = useQuery("fetchAssets", fetchAssets, { enabled: open });
	const { data: currencies } = useQuery("fetchCurrencies", fetchCurrencies, { enabled: open });
	const { data: investments } = useQuery("fetchInvestments", fetchInvestments, {
		enabled: open,
	});

	useEffect(() => {
		if (!open) return;
		setForm(initialValuesRef.current);
		setErrors({});
		setSubmitted(false);
	}, [open, excludeId]);

	const assetOptions = useMemo(() => {
		const set = new Set((assets || []).map(normalizeAsset));
		(investments || []).forEach((row) => set.add(normalizeAsset(row.asset)));
		return Array.from(set).filter(Boolean).sort();
	}, [assets, investments]);

	const currencyOptions = useMemo(() => {
		const all = Object.keys(currencies || {}).sort();
		const current = (form?.currency || "USD").toUpperCase();
		const common = COMMON_CURRENCIES.includes(current)
			? COMMON_CURRENCIES
			: [...COMMON_CURRENCIES, current];
		return { common, rest: all.filter((code) => !common.includes(code)) };
	}, [currencies, form?.currency]);

	// Units still held of this asset, ignoring the row being edited.
	const heldUnits = useMemo(() => {
		const asset = normalizeAsset(form?.asset);
		if (!asset || !investments) return null;
		const rows = investments.filter(
			(row) => normalizeAsset(row.asset) === asset && row.id !== excludeId
		);
		if (!rows.length) return null;
		return rows.reduce((total, row) => total + getSignedUnits(row), 0);
	}, [form?.asset, investments, excludeId]);

	if (!open || !form) return null;

	const isDividend = form.transactionType === "DIVIDEND";
	const isSell = form.transactionType === "SELL";
	const typeOptions = allowDividend
		? TYPE_OPTIONS
		: TYPE_OPTIONS.filter((option) => option.value !== "DIVIDEND");

	const update = (name, value) => {
		const next = { ...form, [name]: value };
		setForm(next);
		if (submitted) setErrors(validateTransaction(next));
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		setSubmitted(true);
		const nextErrors = validateTransaction(form);
		setErrors(nextErrors);
		const firstInvalid = Object.keys(nextErrors)[0];
		if (firstInvalid) {
			document.getElementById(fieldId(firstInvalid))?.focus();
			return;
		}
		onSubmit({
			...form,
			asset: normalizeAsset(form.asset),
			currency: form.currency.toUpperCase(),
			value: parseFloat(form.value),
			units: isDividend ? undefined : parseFloat(form.units),
		});
	};

	const units = parseFloat(form.units);
	const price = parseFloat(form.value);
	const hasTotal = Number.isFinite(units) && Number.isFinite(price);
	const isKnownAsset = assetOptions.includes(normalizeAsset(form.asset));
	const assetHint = !form.asset.trim()
		? "Pick an existing asset or type a new ticker"
		: heldUnits !== null
			? `You hold ${formatUnits(heldUnits)} ${humanizeAssetLabel(form.asset)}${
					excludeId ? " excluding this entry" : ""
				}`
			: isKnownAsset
				? null
				: "New asset — it will be added to your portfolio";
	const oversell =
		isSell && heldUnits !== null && Number.isFinite(units) && units > heldUnits + 1e-9;
	const unitsHint = oversell
		? `More than the ${formatUnits(heldUnits)} units you hold`
		: null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={title}
			description={description}
			onSubmit={handleSubmit}
			footer={
				<>
					<Button type="button" variant="outline" size="sm" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" size="sm" disabled={isSubmitting}>
						{isSubmitting ? "Saving…" : submitLabel}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				<fieldset>
					<legend className="mb-1.5 block text-sm font-medium text-slate-700">Type</legend>
					<div
						className={cn(
							"grid gap-1 rounded-lg bg-slate-100 p-1",
							typeOptions.length === 3 ? "grid-cols-3" : "grid-cols-2"
						)}
					>
						{typeOptions.map(({ value, label, icon: Icon }) => {
							const checked = form.transactionType === value;
							return (
								<label
									key={value}
									className={cn(
										"flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-slate-400",
										checked
											? "bg-white text-slate-900 shadow-sm"
											: "text-slate-500 hover:text-slate-900"
									)}
								>
									<input
										type="radio"
										name={`${idPrefix}-type`}
										value={value}
										checked={checked}
										onChange={() => update("transactionType", value)}
										className="sr-only"
									/>
									<Icon
										className={cn(
											"h-4 w-4",
											checked &&
												(value === "BUY"
													? "text-emerald-600"
													: value === "SELL"
														? "text-rose-600"
														: "text-amber-600")
										)}
										aria-hidden="true"
									/>
									{label}
								</label>
							);
						})}
					</div>
				</fieldset>

				<div className="grid gap-4 sm:grid-cols-2">
					<Field
						id={fieldId("asset")}
						label="Asset"
						hint={assetHint}
						error={errors.asset}
					>
						<input
							id={fieldId("asset")}
							data-autofocus
							list={fieldId("asset-options")}
							type="text"
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							value={form.asset}
							onChange={(event) => update("asset", event.target.value.toLowerCase())}
							placeholder="e.g. apple, gold, vwrl"
							aria-invalid={Boolean(errors.asset)}
							aria-describedby={describedBy(fieldId("asset"), errors.asset, assetHint)}
							className={CONTROL_CLASS}
						/>
						<datalist id={fieldId("asset-options")}>
							{assetOptions.map((asset) => (
								<option key={asset} value={asset}>
									{humanizeAssetLabel(asset)}
								</option>
							))}
						</datalist>
					</Field>
					<Field
						id={fieldId("transactionDate")}
						label="Date"
						error={errors.transactionDate}
					>
						<input
							id={fieldId("transactionDate")}
							type="date"
							value={form.transactionDate}
							onChange={(event) => update("transactionDate", event.target.value)}
							aria-invalid={Boolean(errors.transactionDate)}
							aria-describedby={describedBy(
								fieldId("transactionDate"),
								errors.transactionDate
							)}
							className={CONTROL_CLASS}
						/>
					</Field>
				</div>

				<div className={cn("grid gap-4", isDividend ? "grid-cols-1" : "sm:grid-cols-2")}>
					{!isDividend && (
						<Field
							id={fieldId("units")}
							label="Units"
							hint={unitsHint}
							error={errors.units}
						>
							<input
								id={fieldId("units")}
								type="number"
								inputMode="decimal"
								step="any"
								min="0"
								value={form.units}
								onChange={(event) => update("units", event.target.value)}
								placeholder="0"
								aria-invalid={Boolean(errors.units)}
								aria-describedby={describedBy(fieldId("units"), errors.units, unitsHint)}
								className={cn(CONTROL_CLASS, "tabular-nums", oversell && "border-amber-400")}
							/>
						</Field>
					)}
					<Field
						id={fieldId("value")}
						label={isDividend ? "Amount received" : "Price per unit"}
						error={errors.value}
					>
						<div className="flex">
							<input
								id={fieldId("value")}
								type="number"
								inputMode="decimal"
								step="any"
								min="0"
								value={form.value}
								onChange={(event) => update("value", event.target.value)}
								placeholder="0.00"
								aria-invalid={Boolean(errors.value)}
								aria-describedby={describedBy(fieldId("value"), errors.value)}
								className={cn(CONTROL_CLASS, "min-w-0 rounded-r-none tabular-nums")}
							/>
							<label htmlFor={fieldId("currency")} className="sr-only">
								Currency
							</label>
							<select
								id={fieldId("currency")}
								value={form.currency}
								onChange={(event) => update("currency", event.target.value)}
								aria-invalid={Boolean(errors.currency)}
								className="h-9 shrink-0 cursor-pointer rounded-r-md border border-l-0 border-slate-200 bg-slate-50 pl-2 pr-7 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
							>
								<optgroup label="Common">
									{currencyOptions.common.map((code) => (
										<option key={code} value={code}>
											{code}
										</option>
									))}
								</optgroup>
								{currencyOptions.rest.length > 0 && (
									<optgroup label="All currencies">
										{currencyOptions.rest.map((code) => (
											<option key={code} value={code}>
												{code}
											</option>
										))}
									</optgroup>
								)}
							</select>
						</div>
					</Field>
				</div>

				<div
					className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
					aria-live="polite"
				>
					<div className="min-w-0">
						<p className="text-sm font-medium text-slate-700">
							{isDividend ? "Dividend" : isSell ? "Proceeds" : "Total cost"}
						</p>
						{!isDividend && (
							<p className="truncate text-xs tabular-nums text-slate-500">
								{hasTotal
									? `${formatUnits(units)} × ${formatMoney(price, form.currency)}`
									: "Units × price per unit"}
							</p>
						)}
					</div>
					<p className="text-lg font-semibold tabular-nums text-slate-900">
						{isDividend
							? Number.isFinite(price)
								? formatMoney(price, form.currency)
								: "—"
							: hasTotal
								? formatMoney(units * price, form.currency)
								: "—"}
					</p>
				</div>

				{serverError && (
					<div
						role="alert"
						className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
					>
						<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
						<span>{serverError}</span>
					</div>
				)}
			</div>
		</Dialog>
	);
};
